import React, {
  FC,
  FocusEventHandler,
  KeyboardEventHandler,
  useRef,
} from 'react';
import dompurify from 'dompurify';
import {
  HeadingText,
  Button,
  StackLayout,
  Box,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { SaveStatus } from '../save-status/save-status.component';
import { ValueWithCopyButton } from '../value-with-copy-button/value-with-copy-button.component';

interface EntityNameAndSaveStatusProps {
  entityInstanceName?: string;
  instanceId?: string;
  saveStatusMessage?: string;
  isEntitySaved: boolean;
  isArchived: boolean;
  /**
   * Indicates if the user is able to edit the entity name directly via contentEditable
   */
  isNameEditable?: boolean;
  /**
   * Callback to set the entity's name when isNameEditable is true and user has typed
   */
  setEntityName: (name: string) => void;
}

export const EntityNameAndSaveStatus: FC<EntityNameAndSaveStatusProps> = ({
  entityInstanceName,
  instanceId,
  isArchived,
  saveStatusMessage,
  isEntitySaved,
  isNameEditable = false,
  setEntityName,
}) => {
  const { formatMessage } = useIntl();
  const [showEditNameButtonIcon, setShowEditNameButtonIcon] =
    React.useState<boolean>(true);
  const nameRef = useRef<HTMLSpanElement>(null);
  const onBlur: FocusEventHandler<HTMLSpanElement> = (evt) => {
    setEntityName?.(dompurify.sanitize(evt.currentTarget.innerText.trim()));
    setShowEditNameButtonIcon(true);
  };
  const onKeyDown: KeyboardEventHandler<HTMLSpanElement> = (evt) => {
    if (evt.key === 'Enter') {
      nameRef.current?.blur();
    }
  };

  return (
    <StackLayout
      spacing="$quarter"
      css={{
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      <StackLayout
        orientation="horizontal"
        spacing="$half"
        verticalAlignment="center"
        css={{ width: '100%' }}
      >
        <HeadingText
          level="1"
          size="lg"
          css={{
            overflow: 'hidden',
            ...(showEditNameButtonIcon && { textOverflow: 'ellipsis' }),
            whiteSpace: 'nowrap',
          }}
        >
          {isNameEditable ? (
            <Box
              as="span"
              ref={nameRef}
              role="textbox"
              contentEditable
              tabIndex={0}
              aria-label={formatMessage({ id: 'ENTITY_NAME' })}
              suppressContentEditableWarning
              onBlur={onBlur}
              onKeyDown={onKeyDown}
              onFocus={() => setShowEditNameButtonIcon(false)}
            >
              {entityInstanceName}
            </Box>
          ) : (
            entityInstanceName
          )}
        </HeadingText>
        {isNameEditable && showEditNameButtonIcon && (
          <Button
            data-testid="editNameButton"
            priority="secondary"
            quiet
            size="toolbar"
            icon="interfaceEdit"
            hideLabel
            css={{ borderColor: 'transparent' }}
            onClick={() => {
              nameRef.current?.focus();
            }}
          >
            {formatMessage({ id: 'EDIT_ENTITY_NAME' })}
          </Button>
        )}
      </StackLayout>
      <StackLayout
        orientation="horizontal"
        spacing="$half"
        verticalAlignment="center"
        css={{ minHeight: 32 }}
      >
        {instanceId && (
          <ValueWithCopyButton
            value={instanceId}
            buttonLabel={formatMessage({ id: 'COPY_ENTITY_ID' })}
          />
        )}
        {!isArchived && saveStatusMessage && (
          <SaveStatus
            isEntitySaved={isEntitySaved}
            saveStatusMessage={saveStatusMessage}
          />
        )}
      </StackLayout>
    </StackLayout>
  );
};
